import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ProductGallery } from "@/components/ProductGallery";
import { formatPHP } from "@/lib/api";
import { CATEGORY_BADGE, CATEGORY_LABEL } from "@/data/site";
import { Check, Waves, Citrus, Sprout, Droplets, Leaf, TicketPercent, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { ComplianceNote, DEFAULT_COMPLIANCE_NOTE } from "@/components/ComplianceNote";

const INGREDIENT_ICONS = { waves: Waves, citrus: Citrus, sprout: Sprout, droplets: Droplets, leaf: Leaf };

export const ProductDetailDialog = ({ product, open, onClose, onOrder, gcashEnabled = false, priceLabel = "Promo Price", promoNote = "", disclaimer = DEFAULT_COMPLIANCE_NOTE }) => {
  if (!product) return null;
  const ingredients = product.ingredients || [];
  const variants = product.variants || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-2xl p-0" data-testid="product-detail-dialog">
        <div className="flex flex-col">
          <div className="product-media bg-gradient-to-br from-secondary/60 to-muted">
            <ProductGallery
              images={product.images && product.images.length ? product.images : (product.imageUrl ? [product.imageUrl] : [])}
              alt={product.name}
              fallbackLabel={product.line}
              ratio={16 / 9}
              imageClassName="p-4"
              showArrows
              testid="detail-gallery"
            />
          </div>
          <div className="flex flex-col p-6">
            <span className={cn("inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium", CATEGORY_BADGE[product.category])}>
              {CATEGORY_LABEL[product.category]} • {product.line}
            </span>
            <h3 className="font-display mt-3 text-2xl font-semibold text-foreground">{product.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{product.tagline}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground/80">{product.description}</p>

            <ul className="mt-4 space-y-2">
              {(product.benefits || []).map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-foreground/80">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {ingredients.length > 0 && (
              <div className="mt-5" data-testid="ingredient-strip">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mga Aktibong Sangkap</p>
                <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {ingredients.map((ing) => {
                    const Icon = INGREDIENT_ICONS[ing.icon] || Leaf;
                    return (
                      <div
                        key={ing.name}
                        className="flex items-center gap-2.5 rounded-xl border border-border bg-secondary/50 p-2"
                        data-testid={`ingredient-item-${ing.name}`}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-foreground">{ing.name}</p>
                          {ing.sci ? <p className="truncate text-[10px] italic leading-tight text-muted-foreground">{ing.sci}</p> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {variants.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2" data-testid="variant-preview">
                {variants.map((g) => (
                  <div key={g.name}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g.name}</p>
                    <p className="mt-1 text-sm text-foreground/80">{g.options.join(" • ")}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-auto pt-6">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-display text-2xl font-semibold text-foreground">{formatPHP(product.pricePHP)}</span>
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/12 px-2.5 py-0.5 text-xs font-semibold text-accent"
                  data-testid="detail-promo-badge"
                >
                  <TicketPercent className="h-3.5 w-3.5" /> {priceLabel}
                </span>
              </div>
              {promoNote ? (
                <p className="mt-2 flex items-start gap-1.5 text-xs font-medium leading-relaxed text-accent" data-testid="detail-promo-note">
                  <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{promoNote}</span>
                </p>
              ) : null}
              <Button
                className="mt-4 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => onOrder(product)}
                data-testid="detail-order-button"
              >
                {gcashEnabled ? "Bumili via GCash" : "I-order / Mag-inquire"}
              </Button>
              <div className="mt-3 flex justify-center">
                <ComplianceNote text={disclaimer} testid="detail-compliance-note" />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
